// A CLIENT-BORNE EXPENSE WHOSE CHARGE EXISTS — shown in the expense dialog where the price
// would be, and corrected from right here.
//
// Once charged, the price and the bearer are the invoice's business (the server refuses
// either change: expense_charge_locked). The dialog used to grey both out and say "go to
// the money screen", which left the owner hunting for the charge. Now it shows the charge
// as it really is — the INVOICE's amount, never `charge_amount`, which is only the
// price typed at entry and goes stale the moment the charge is corrected — and opens the
// money screen's own dialogs on it. Same components, same endpoints, same guards: the
// amount may not drop below what was collected, and only a charge with nothing collected
// can be cancelled. Nothing here computes a figure. No date and no collection state: the
// dialog is about the supplier side, and whether the client has paid is the money screen's.
//
// The bearer stays locked; the line under it says what has to happen first for it to
// change — cancel the charge, or, once money has come in, undo the payment.
import he from '../i18n/he';
import Money from './Money';
import Button from './Button';
import { Field } from './Form';
import CorrectChargeModal from './CorrectChargeModal';
import CancelChargeModal from './CancelChargeModal';

const t = he.expenses;

// The charge as the money screen's dialogs describe one, read from the expense row's own
// join (queries/expenses.js CHARGED_COLS). A job charge carries no month.
export const chargeRowOf = (e) => ({
  invoiceId: e.charged_on_invoice_id,
  amount: Number(e.charged_invoice_amount) || 0,
  issueDate: e.charged_invoice_date,
  issuingPartnerId: e.charged_invoice_issuer_id,
  paid: Number(e.charged_invoice_paid) || 0,
  linkedCosts: Number(e.charged_invoice_linked_costs) || 0,
  periodMonth: null,
});

export default function ExpenseChargeExisting({ expense, onAct }) {
  const collection = expense.charge_collection;
  // A cost still pointing at a cancelled invoice is an anomaly verify:income reports; it
  // offers nothing to correct.
  if (collection && collection.cancelled) return null;
  const row = chargeRowOf(expense);
  const collected = row.paid > 0;
  return (
    <Field label={t.chargeExisting}>
      <div className="row">
        <strong><Money value={row.amount} /></strong>
      </div>
      <div className="row" style={{ marginTop: 'var(--sp-2)' }}>
        <Button small variant="subtle" onClick={() => onAct('correct')}>{t.chargeCorrect}</Button>
        {!collected && (
          <Button small variant="subtle" onClick={() => onAct('cancel')}>{t.chargeCancel}</Button>
        )}
      </div>
      <p className="muted small-note">{collected ? t.bearerNeedsUnpay : t.bearerNeedsCancel}</p>
    </Field>
  );
}

// The money screen's dialog, opened in place of the expense dialog. `owner` names the job
// the way the money screen's card does (its title and client).
export function ChargeActDialog({ act, expense, owner, onClose, onSaved }) {
  const card = { title: owner?.title || expense.description || '', clientName: owner?.clientName || null };
  const row = chargeRowOf(expense);
  const Dialog = act === 'cancel' ? CancelChargeModal : CorrectChargeModal;
  return <Dialog card={card} row={row} onClose={onClose} onSaved={onSaved} />;
}
